/** Determinate or indeterminate progress bar with optional label. Wraps React Aria ProgressBar. */
import {
  ProgressBar as AriaProgressBar,
  Label,
  type ProgressBarProps as AriaProgressBarProps,
} from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/foundation/utils/cn';

const trackVariants = cva('w-full bg-[var(--f-bg-sunken)] rounded-full overflow-hidden', {
  variants: {
    size: {
      sm: 'h-1.5',
      md: 'h-2.5',
      lg: 'h-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface ProgressBarProps extends AriaProgressBarProps, VariantProps<typeof trackVariants> {
  label?: string;
  showValue?: boolean;
  className?: string;
}

export function ProgressBar({
  label,
  showValue = false,
  size,
  className,
  ...props
}: ProgressBarProps) {
  return (
    <AriaProgressBar className={cn('flex flex-col gap-1', className)} {...props}>
      {({ percentage, valueText, isIndeterminate }) => (
        <>
          {(label || showValue) && (
            <div className="flex justify-between items-center">
              {label && (
                <Label className="text-sm font-medium text-[var(--f-fg-secondary)]">{label}</Label>
              )}
              {showValue && <span className="text-sm text-[var(--f-fg-muted)]">{valueText}</span>}
            </div>
          )}
          <div className={trackVariants({ size })}>
            <div
              className={cn(
                'h-full bg-[var(--f-accent)] rounded-full transition-all duration-500 ease-out',
                isIndeterminate && 'animate-pulse w-full'
              )}
              style={!isIndeterminate ? { width: `${percentage}%` } : undefined}
            />
          </div>
        </>
      )}
    </AriaProgressBar>
  );
}
